import { useEffect, useState } from 'react';

/**
 * Theme control for the app while it runs inside an iframe (Admin's "login as
 * client" panels).
 *
 * Embedded, the app has no business following the *admin's* OS colour scheme:
 * the host page is its own dark UI, and the default `system` theme made every
 * impersonated view render dark regardless of what the client would see. So the
 * embedded case pins light, and the host asks for dark explicitly by posting a
 * message to the frame.
 *
 * The value is returned for `next-themes`' `forcedTheme`, not `setTheme`, so it
 * never writes to storage — a client's own saved preference survives being
 * previewed by an admin.
 */

export const EMBEDDED_THEME_MESSAGE = 'askable:embedded-theme';
export const EMBEDDED_THEME_READY_MESSAGE = 'askable:embedded-theme-ready';

export type EmbeddedTheme = 'light' | 'dark';

function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin `window.top` that refuses comparison only happens when
    // there *is* an ancestor frame.
    return true;
  }
}

function readTheme(data: unknown): EmbeddedTheme | null {
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const message = data as { type?: unknown; theme?: unknown };
  if (message.type !== EMBEDDED_THEME_MESSAGE) {
    return null;
  }
  return message.theme === 'dark' || message.theme === 'light' ? message.theme : null;
}

export function useEmbeddedTheme(): EmbeddedTheme | undefined {
  // Frame-ness can't change for the life of the document, so it's read once.
  const [embedded] = useState(isEmbedded);
  const [theme, setTheme] = useState<EmbeddedTheme>('light');

  useEffect(() => {
    if (!embedded) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      // Only the embedder gets to drive this; origin isn't checked because the
      // host is served from a different subdomain per environment and the
      // payload carries nothing but a colour scheme.
      if (event.source !== window.parent) {
        return;
      }
      const next = readTheme(event.data);
      if (next) {
        setTheme(next);
      }
    };

    window.addEventListener('message', handleMessage);

    // The host can't know when this listener attaches — `iframe.onload` fires
    // before React mounts — so the frame announces itself and the host replies
    // with the theme it wants.
    window.parent.postMessage({ type: EMBEDDED_THEME_READY_MESSAGE }, '*');

    return () => window.removeEventListener('message', handleMessage);
  }, [embedded]);

  return embedded ? theme : undefined;
}
